import config from "../config";
import { usersService } from "../services/users.service";

const POPUP_WIDTH = 620;
const POPUP_HEIGHT = 760;

const RESULT_TYPES = [
  "mailoauth_linked",
  "mailoauth_error",
  "mailoauth_admin_consent_required",
];

const buildAllowedOrigins = () => {
  const origins = new Set([window.location.origin]);
  if (config?.apiBaseUrl) {
    try {
      origins.add(new URL(config.apiBaseUrl).origin);
    } catch {
      return origins;
    }
  }
  return origins;
};

const closePopup = (popup) => {
  try {
    popup.close();
  } catch (closeError) {
    return false;
  }
  return true;
};

const resolveResultType = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.type) return data.type;
  if (data.error === "admin_consent_required") {
    return "mailoauth_admin_consent_required";
  }
  if (data.error === "oauth_error") return "mailoauth_error";
  return null;
};

export function openMailOAuthPopup({ provider, account, onResult, onClosed }) {
  const left = window.screenX + Math.max(0, (window.outerWidth - POPUP_WIDTH) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - POPUP_HEIGHT) / 2);

  const popup = window.open(
    "",
    `${provider}_oauth_popup_${account?.id ?? "reauth"}`,
    `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  );

  const reportError = (message) => {
    if (typeof onResult === "function") {
      onResult({ type: "mailoauth_error", linked: false, message });
    }
  };

  if (!popup) {
    window.setTimeout(() => {
      reportError(
        "El navegador bloqueó la ventana de conexión. Habilitá los popups para este sitio y volvé a intentar.",
      );
    }, 0);
    return null;
  }

  Promise.resolve(usersService.getMailOAuthUrl({ provider, account }))
    .then((url) => {
      if (!url) throw new Error("URL de conexión vacía");
      if (!popup.closed) {
        popup.location = url;
      }
    })
    .catch(() => {
      if (typeof onResult === "function") {
        closePopup(popup);
        reportError("No se pudo iniciar la conexión de la casilla.");
        return;
      }
      try {
        popup.document.body.textContent =
          "No se pudo iniciar la conexión de la casilla. Cerrá esta ventana y probá de nuevo.";
      } catch {
        closePopup(popup);
      }
    });

  let timer = null;
  let onMessage = null;

  const stopWatching = () => {
    if (timer) {
      window.clearInterval(timer);
      timer = null;
    }
    if (onMessage) {
      window.removeEventListener("message", onMessage);
      onMessage = null;
    }
  };

  if (typeof onResult === "function") {
    const allowedOrigins = buildAllowedOrigins();

    onMessage = (event) => {
      if (!allowedOrigins.has(event.origin)) return;
      if (event.source !== popup) return;

      const type = resolveResultType(event.data);
      if (!RESULT_TYPES.includes(type)) return;

      stopWatching();
      closePopup(popup);
      onResult({
        type,
        linked: type === "mailoauth_linked",
        message: event.data?.message || event.data?.detail || null,
      });
    };

    window.addEventListener("message", onMessage);
  }

  if (typeof onClosed === "function" || onMessage) {
    timer = window.setInterval(() => {
      if (!popup || popup.closed) {
        stopWatching();
        if (typeof onClosed === "function") onClosed();
      }
    }, 500);
  }

  return popup;
}

export default openMailOAuthPopup;
